import { useCallback } from 'react';

import { useField } from 'formik';

import { UseChargeInputValidationProps } from './types';

export default function useChargeInputValidation({
    name,
    min,
    max,
    finalErrors,
    hasError,
    addError,
    clearErrors,
}: UseChargeInputValidationProps) {
    const [field, meta, helper] = useField(name);

    const validate = useCallback(
        (value: number) => {
            let highestValue = max;
            let lowestValue = min;

            // Cater for user errors when making lowest higher than the highest allowed
            if (max < min) {
                highestValue = min;
                lowestValue = max;
            }

            if (value < lowestValue || value > highestValue) {
                return `Value must be between ${lowestValue} and ${highestValue}`;
            }
            return undefined; // No error
        },
        [min, max]
    );

    if (field.value === undefined) {
        // add blank space to align boxes to correct column
        return {
            shouldRenderEmptyBox: true,
        };
    }

    const hasFormError = meta.touched && meta.error;
    const displayError = hasError || hasFormError;
    const errorMessage = hasError ? finalErrors[0] : meta.error;

    const { setError, setTouched, setValue } = helper;

    const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const numberValue = Number(event.target.value);
        setValue(numberValue);

        const validationError = validate(numberValue);
        if (validationError) {
            addError(validationError);
            setError(validationError);
        } else {
            clearErrors();
            setError(undefined);
        }

        setTouched(true);
    };

    const handleBlurCurrency = () => {
        setTouched(true);
        const validationError = validate(field.value);

        if (validationError) {
            addError(validationError);
            setError(validationError);
        } else {
            clearErrors();
            setError(undefined);
        }
    };

    return {
        shouldRenderEmptyBox: false,
        field,
        meta,
        helper,
        displayError,
        errorMessage,
        handleCurrencyChange,
        handleBlurCurrency,
        validate,
        emptyBoxComponent: null,
    };
}
